/**
 * Page maths for the progress dashboards.
 *
 * The lists behind Goals, Practice, Reflections and Activity grow without
 * limit — someone with 30 completed goals should not have to scroll past all
 * 30 to reach the rest of the page. Everything is shown a page at a time with
 * a numbered pager instead.
 *
 * Deliberately free of imports so the rules can be tested on their own with
 * `node --test` — see tests/growth/paging.test.ts.
 */

/** Rows shown per page. Small on purpose: these sit inside a scrolling sheet. */
export const PAGE_SIZE = 5;

/** How many numbered buttons the pager shows before it starts eliding. */
export const PAGE_WINDOW = 5;

/** A gap in the pager, rendered as "…" — one or more page numbers are hidden. */
export const PAGE_GAP = "gap";

export type PageSlot = number | typeof PAGE_GAP;

export function pageCount(totalItems: number, pageSize = PAGE_SIZE): number {
  if (totalItems <= 0 || pageSize <= 0) return 0;
  return Math.ceil(totalItems / pageSize);
}

/** Keeps a remembered page in range when the list behind it shrinks. */
export function clampPage(page: number, totalPages: number): number {
  if (totalPages <= 0) return 1;
  if (!Number.isFinite(page)) return 1;
  return Math.min(Math.max(Math.trunc(page), 1), totalPages);
}

export function pageSlice<T>(
  items: T[],
  page: number,
  pageSize = PAGE_SIZE,
): T[] {
  const start = (clampPage(page, pageCount(items.length, pageSize)) - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

/**
 * The buttons to draw for `current` of `total` pages.
 *
 * Up to `PAGE_WINDOW` numbers are shown. Past that the first and last pages are
 * always reachable and the middle slides with the user, so the row never grows
 * wider than seven chips no matter how many pages there are:
 *
 *   page 1 of 9   → 1 2 3 4 … 9
 *   page 5 of 9   → 1 … 4 5 6 … 9
 *   page 9 of 9   → 1 … 6 7 8 9
 *
 * `hasMore` marks a list that is paged from the server and has not been fully
 * loaded: a trailing gap is shown because the real last page isn't known yet.
 */
export function pageWindow(
  current: number,
  total: number,
  hasMore = false,
): PageSlot[] {
  if (total <= 0) return [];
  const page = clampPage(current, total);
  if (total <= PAGE_WINDOW) {
    const all: PageSlot[] = [];
    for (let p = 1; p <= total; p += 1) all.push(p);
    if (hasMore) all.push(PAGE_GAP);
    return all;
  }

  const slots: PageSlot[] = [1];
  // Three sliding numbers between the fixed first and last page.
  let start = Math.max(2, page - 1);
  let end = Math.min(total - 1, page + 1);
  // Near either end, spend the freed slots on the other side so the row keeps a
  // steady width rather than shrinking on the first and last pages.
  if (page <= 3) end = 4;
  if (page >= total - 2) start = total - 3;

  if (start > 2) slots.push(PAGE_GAP);
  for (let p = start; p <= end; p += 1) slots.push(p);
  if (end < total - 1) slots.push(PAGE_GAP);
  slots.push(total);
  if (hasMore) slots.push(PAGE_GAP);
  return slots;
}
